import React, { useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  successOrSelector,
  pharmacistActions,
  getReplacementPDF,
  resetStatus,
  TypeOfUser,
  IReplacementsData,
  IPharmacyReplacementData,
  getPharmacyReplacementPdf,
  pharmacyActions,
  getPharmacyReplacementsWeb,
  ITEMS_PER_PAGE,
  getPharmacistReplacementsWeb,
  ToUserTypings,
  Agencies,
  AdditionalReports,
} from '@pharmaplan/common';

import { FormikValues, useFormik } from 'formik';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import strings from '../../localization';
import { downloadPDF, setPageTitle } from '../../helpers/Functions';
import ResponsiveClasses from '../../theme/ResponsiveClasses';
import useGetItems from '../../hooks/useGetItems';
import { resetDialog, setDialog } from '../../reducers/dialogReducer';
import useUser from '../../hooks/useUser';

import DynamicTable from '../DynamicTable';
import AdsContainer from '../common/AdsContainer';
import DownloadPDF from '../common/DownloadPDF';

import {
  headers,
  pharmacistHeaders,
  pharmacistReplacementMapper,
  pharmacyReplacementMapper,
} from './helpers';
import { renderScreen, setOpen } from '../../actions/drawerActions';
import { OtherScreens, ScreenTypes } from '../../helpers/Constants';
import Drawer from '../Drawer';
import useTableSort from '../../hooks/useTableSort';
import BookingReport from '../Invoice/BookingReport';
import useSelfService from '../../hooks/useSelfService';
import { replacementFilter } from '../../helpers/Filters';
import { setDisableSubmitFilter } from '../../reducers/mainCalendarReducer';
import useExpandableFilter from '../../hooks/useExpandableFilter';
import CustomButton from '../common/CustomButton';
import { IDynamicTableHeaderBar } from '../DynamicTable/types';

const successActions = [
  pharmacyActions.addIncompatible,
  pharmacyActions.removeIncompatible,
];

const MyReplacements = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isFiltered, setIsFiltered] = useState(false);

  const { isDesc, orderBy, handleSorting, order } = useTableSort();
  const { replacements: repl, pdf, userType } = useUser();
  const { isSelfService } = useSelfService();
  const { getImage } = useGetItems();

  const isPharmacy = userType === TypeOfUser.pharmacy;
  const isPharmacist = userType === TypeOfUser.pharmacist;

  const goToPharmacistDetails = (pharmacistId: string, isAutoSyncRecord?: boolean) =>
    () => {
      dispatch(setOpen(true));
      dispatch(
        renderScreen({
          screenNumber: 1,
          screenType: ScreenTypes.replacement,
          pharmacistId,
          extraParameters: {
            isAutoSyncRecord,
          },
        }),
      );
    };

  const goToPharmacistRating = (pharmacistId: string) =>
    () => {
      dispatch(setOpen(true));
      dispatch(
        renderScreen({
          screenNumber: 4,
          screenType: ScreenTypes.workshiftSummary,
          pharmacistId,
          fromScreen: OtherScreens.Replacements,
        }),
      );
    };

  const loadSuccess = useAppSelector((state) =>
    successOrSelector(
      [pharmacistActions.replacementsWeb, pharmacyActions.replacementsWeb],
      state,
    ));

  const pdfSuccess = useAppSelector((state) =>
    successOrSelector(
      [pharmacistActions.getReplacementPDF, pharmacyActions.replacementsPdf],
      state,
    ));

  const success = useAppSelector((state) =>
    successOrSelector(successActions, state));

  const [page, setPage] = useState(1);

  const currentIndex = (page - 1) * ITEMS_PER_PAGE;

  const handleAPI = (
    sortBy?: string,
    desc?: boolean,
    values?: FormikValues,
  ) => {
    if (isPharmacy) {
      dispatch(
        getPharmacyReplacementsWeb({
          page: 1,
          sortBy,
          desc,
          ...values,
        }),
      );
      return;
    }

    dispatch(
      getPharmacistReplacementsWeb({
        page: 1,
        sortBy,
        ...values,
        desc,
      }),
    );
  };

  const defaultAgency = isSelfService
    ? Agencies.PharmaGap
    : Agencies.PharmaPlan;
  const formik = useFormik({
    initialValues: {
      source: defaultAgency,
    },
    onSubmit: (values) => {
      setIsFiltered(true);
      handleAPI(orderBy, isDesc, values);
    },
  });

  const openBookingReport = (
    pharmacyId: string,
    isAutoSyncRecord?: boolean,
  ) => {
    dispatch(
      setDialog({
        Component: (
          <BookingReport
            isAutoSyncRecord={isAutoSyncRecord}
            pharmacyId={pharmacyId}
          />
        ),
        heading: {
          title: strings.bookingReport,
        },
        showCloseButton: false,
        maxWidth: 'lg',
      }),
    );
  };

  const pharmacistConfig = {
    title: strings.pharmaciesServed,
    headers: pharmacistHeaders(!!repl.data?.[0]?.isAutoSyncRecord),
    rows: pharmacistReplacementMapper(
      repl.data as IReplacementsData[],
      navigate,
      currentIndex,
      getImage,
      goToPharmacistRating,
      openBookingReport,
      formik.values?.source,
    ),
    request: getPharmacistReplacementsWeb,
    pdfRequest: getReplacementPDF,
  };

  const userConfig = ToUserTypings({
    [TypeOfUser.pharmacist]: { ...pharmacistConfig },
    [TypeOfUser.technician]: { ...pharmacistConfig },
    [TypeOfUser.student]: { ...pharmacistConfig },
    [TypeOfUser.pharmacy]: {
      title: isSelfService
        ? strings.myReliefProfessionals
        : strings.formatString(
          strings.myReplacementUser,
          strings.pharmacistsLower,
        ),
      headers: headers(!!repl.data?.[0]?.externalPharmacistId),
      rows: pharmacyReplacementMapper(
        repl.data as IPharmacyReplacementData[],
        navigate,
        currentIndex,
        goToPharmacistDetails,
        goToPharmacistRating,
        formik.values?.source,
      ),
      request: getPharmacyReplacementsWeb,
      pdfRequest: getPharmacyReplacementPdf,
    },
  });

  const { showFilter, setShowFilter, filterStates, showFilterIcon } = useExpandableFilter(formik, isFiltered, AdditionalReports.replacements);

  const handleClear = () => {
    formik.resetForm();
    setIsFiltered(false);
    handleAPI();
  };

  const Table = {
    title: userConfig[userType].title as string,
    headers: userConfig[userType].headers,
    headerBar: [
      {
        Component: (
          <CustomButton
            label={strings.clear}
            variant="outlined"
            onClick={() => {
              handleClear();
            }}
          />
        ),
        key: '0',
        name: 'clear',
        display: !isFiltered,
      },
      {
        key: '1',
        Component: (
          <DownloadPDF
            onClick={() =>
              dispatch(userConfig[userType].pdfRequest(formik.values?.source))}
          />
        ),
      },
      showFilterIcon && {
        icon: filterStates()?.icon,
        key: '2',
        name: 'filter',
        onClick: filterStates()?.onClick,
      },
    ].filter(Boolean) as IDynamicTableHeaderBar[],
    rows: userConfig[userType].rows,
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    setPage(selectedPage + 1);
    dispatch(
      userConfig[userType].request({
        page: selectedPage + 1,
        sortBy: orderBy,
        desc: isDesc,
      }),
    );
  };

  useEffect(() => {
    if (pdfSuccess) {
      downloadPDF(pdf.pdfContent, 'replacements.pdf');
      dispatch(
        resetStatus([
          pharmacistActions.getReplacementPDF,
          pharmacyActions.replacementsPdf,
        ]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfSuccess, pdf.pdfContent]);

  useEffect(() => {
    setPageTitle(strings.myPharmacies);
    handleAPI();
    dispatch(setDisableSubmitFilter(true));
    setShowFilter(
      (isSelfService && isPharmacist) || (!isSelfService && isPharmacy),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      handleAPI();
      dispatch(resetDialog());
      dispatch(resetStatus(successActions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      handleAPI(orderBy, isDesc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order, userType]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} lg={10}>
        <DynamicTable
          table={Table}
          filter={replacementFilter(handleClear)}
          page={page}
          loadSuccess={loadSuccess}
          count={repl.count}
          handlePagination={handlePagination}
          totalCount={repl.totalCount}
          order={order}
          orderBy={orderBy}
          showFilter={showFilter}
          formik={formik}
          handleSort={handleSorting}
        />
      </Grid>
      <Grid sx={ResponsiveClasses.hideOnLg} item xs={2}>
        <AdsContainer />
      </Grid>
      <Drawer />
    </Grid>
  );
};

export default MyReplacements;
